import React, { useState, useEffect } from 'react';
import {
  Briefcase, MapPin, Clock, Users,
  FileText, Calendar, X, Eye,
  Building, GraduationCap, Award, Zap, CheckCircle,
  Plus, Edit, Trash2, ChevronDown, ChevronUp, PlusCircle, MinusCircle, ToggleLeft, ToggleRight
} from 'lucide-react';
import '../css/Careerposting.css';
import axios from 'axios';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Convert a bullet-array to a newline string before sending to API */
const arrayToText = (arr) => arr.filter(Boolean).join('\n');

/** Convert a newline string (from API) back to an array for editing */
const textToArray = (str) =>
  typeof str === 'string' ? str.split('\n').filter(Boolean) : (Array.isArray(str) ? str : ['']);

// ─── constants ───────────────────────────────────────────────────────────────

const BULLET_FIELDS = ['responsibilities', 'experienceQualifications', 'mustHave', 'goodToHave'];

const EMPTY_FORM = {
  jobTitle: '',
  category: '',
  employmentType: 'Full-time',
  location: '',
  experience: '',
  role: '',
  reportingTo: '',
  teamName: '',
  // paragraph fields
  aboutRole: '',
  //aboutRolePoints: [''],
  additionalInfo: '',
  // bullet-list fields stored as arrays while editing
  responsibilities: [''],
  experienceQualifications: [''],
  mustHave: [''],
  goodToHave: [''],
  status: 'Active',
};

// ─── DynamicBulletField ──────────────────────────────────────────────────────

const DynamicBulletField = ({ label, icon: Icon, fieldName, items, onChange, required }) => {
  const handleItemChange = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    onChange(fieldName, updated);
  };

  const handleAddItem = () => {
    onChange(fieldName, [...items, '']);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      onChange(fieldName, ['']);
      return;
    }
    onChange(fieldName, items.filter((_, i) => i !== index));
  };

  return (
    <div className="form-group full-width">
      <label>
        {Icon && <Icon size={16} />}
        {label}{required ? ' *' : ''}
      </label>
      <div className="bullet-field-list">
        {items.map((item, index) => (
          <div key={index} className="bullet-field-row">
            <span className="bullet-dot">•</span>
            <input
              type="text"
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder={`Point ${index + 1}`}
              required={required && index === 0}
              className="bullet-input"
            />
            <button
              type="button"
              className="bullet-action-btn remove"
              onClick={() => handleRemoveItem(index)}
              title="Remove point"
            >
              <MinusCircle size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="bullet-add-btn"
          onClick={handleAddItem}
        >
          <PlusCircle size={16} />
          Add Point
        </button>
      </div>
    </div>
  );
};

// ─── BulletDisplay (for card view) ───────────────────────────────────────────

const BulletDisplay = ({ items }) => {
  const list = Array.isArray(items) ? items : textToArray(items);
  return (
    <ul className="bullet-display-list">
      {list.filter(Boolean).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const Careerposting = () => {
  const [showForm, setShowForm] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showPreview, setShowPreview] = useState(false);
  const [togglingJobId, setTogglingJobId] = useState(null); // tracks which job is mid-toggle

  useEffect(() => {
    const fetchjobs = async () => {
      console.log('entered fetching jobs');
      try {
        const response = await axios.get('/api/careers');
        console.log('response from fetch jobs', response.data);
        setJobs(response.data.data);
      } catch (error) {
        alert('Failed to fetch jobs');
        console.log('error for fetchjobs', error);
      }
    };
    fetchjobs();
  }, []);

  // ── input handlers ──────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBulletChange = (fieldName, updatedArray) => {
    setFormData(prev => ({ ...prev, [fieldName]: updatedArray }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setShowPreview(false);
  };

   // ── toggle enable/disable ────────────────────────────────────────────────

const handleToggleStatus = async (job) => {
  const newStatus = job.status === 'Active' ? 'Inactive' : 'Active';
  setTogglingJobId(job.id);
  try {
    await axios.put(`/api/editcareerposts/${job.id}`, {
      jobTitle: job.jobTitle,
      category: job.category,
      location: job.location,
      employmentType: job.employmentType,
      experience: job.experience,
      role: job.role,
      reportingTo: job.reportingTo,
      teamName: job.teamName,
      aboutRole: job.aboutRole,
      responsibilities: job.responsibilities,
      experienceQualifications: job.experienceQualifications,
      mustHave: job.mustHave,
      goodToHave: job.goodToHave,
      additionalInfo: job.additionalInfo,
      status: newStatus,  // only this changes
    });
    setJobs(prev =>
      prev.map(j => j.id === job.id ? { ...j, status: newStatus } : j)
    );
  } catch (err) {
    alert('Failed to update job status');
    console.error('Toggle status error:', err);
  } finally {
    setTogglingJobId(null);
  }
};

  // ── submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert bullet arrays → newline strings before sending to API
    const payload = { ...formData };
    BULLET_FIELDS.forEach(f => {
      payload[f] = arrayToText(formData[f]);
    });

    try {
      let res;
      if (editingJobId) {
        res = await axios.put(`/api/editcareerposts/${editingJobId}`, payload);
        console.log('Update response:', res.data);
        setJobs(prev => prev.map(job => job.id === editingJobId ? res.data : job));
        alert('Job updated successfully!');
        setEditingJobId(null);
      } else {
        res = await axios.post('/api/careerposts', payload);
        console.log('Create response:', res.data);
        setJobs(prev => [...prev, res.data]);
        alert('Job posted successfully!');
      }
    } catch (error) {
      alert('Job submission failed');
      console.error('Submit error:', error);
    }
    resetForm();
    setShowForm(false);
  };

  // ── CRUD helpers ─────────────────────────────────────────────────────────

  const handleAddNew = () => {
    resetForm();
    setEditingJobId(null);
    setShowForm(true);
    setExpandedJobId(null);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      resetForm();
      setShowForm(false);
      setEditingJobId(null);
    }
  };

  const handleEditJob = (job) => {
    setFormData({
      jobTitle: job.jobTitle || '',
      category: job.category || '',
      employmentType: job.employmentType || 'Full-time',
      location: job.location || '',
      experience: job.experience || '',
      role: job.role || '',
      reportingTo: job.reportingTo || '',
      teamName: job.teamName || '',
      aboutRole: job.aboutRole || '',
      //aboutRolePoints: textToArray(job.aboutRolePoints),
      additionalInfo: job.additionalInfo || '',
      // Convert stored newline strings → arrays for editing
      responsibilities: textToArray(job.responsibilities),
      experienceQualifications: textToArray(job.experienceQualifications),
      mustHave: textToArray(job.mustHave),
      goodToHave: textToArray(job.goodToHave),
      status: job.status || 'Active',
    });
    setEditingJobId(job.id);
    setShowForm(true);
    setExpandedJobId(null);
  };

  

  const handleDeleteJob = async (jobId) => {
    try {
      if (window.confirm('Are you sure you want to delete this job posting?')) {
        await axios.delete(`/api/deletecareer/${jobId}`);
        setJobs(jobs.filter(job => job.id !== jobId));
        alert('Job deleted successfully!');
      }
    } catch (err) {
      console.log('error', err);
      alert('Failed to delete the job');
    }
  };

  const toggleJobDetails = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  // ── empty state ──────────────────────────────────────────────────────────

  if (jobs.length === 0 && !showForm) {
    return (
      <div className="job-posting-container">
        <div className="empty-state">
          <div className="empty-icon"><Briefcase size={64} /></div>
          <h2>No Job Postings Yet</h2>
          <p>Start by creating your first job posting to attract talented candidates</p>
          <button className="add-job-btn primary" onClick={handleAddNew}>
            <Plus size={20} /> Add Your First Job
          </button>
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className="job-posting-container">
      {!showForm ? (
        <>
          {/* ── Job List Header ── */}
          <div className="jobs-header">
            <div className="header-info">
              <h1>Job Postings</h1>
              <p>Manage all your career opportunities</p>
            </div>
            <button className="add-job-btn primary" onClick={handleAddNew}>
              <Plus size={20} /> Add New Job
            </button>
          </div>

          {/* ── Jobs List ── */}
          <div className="jobs-list">
            {jobs.map((job) => (
              <div key={job.id} className={`job-card ${expandedJobId === job.id ? 'expanded' : ''}`}>

                {/* Card Header */}
                <div className="job-card-header" onClick={() => toggleJobDetails(job.id)}>
                  <div className="job-card-left">
                    <div className="job-icon"><Briefcase size={24} /></div>
                    <div className="job-basic-info">
                      <h3 className="job-title">{job.jobTitle}</h3>
                      <div className="job-meta-quick">
                        <span className="meta-badge"><Building size={14} />{job.category}</span>
                        <span className="meta-badge"><MapPin size={14} />{job.location}</span>
                        <span className="meta-badge"><Clock size={14} />{job.employmentType}</span>
                        <span className="meta-badge"><GraduationCap size={14} />{job.experience}</span>
                      </div>
                    </div>
                  </div>
                  <div className="job-card-right">
                   <button
                      className={`status-toggle-btn ${job.status === 'Active' ? 'status-toggle-btn--active' : 'status-toggle-btn--inactive'}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleStatus(job); }}
                      disabled={togglingJobId === job.id}
                      title={job.status === 'Active' ? 'Disable (hide from website)' : 'Enable (show on website)'}
                    >
                      {job.status === 'Active'
                        ? <><ToggleRight size={22} /> Enabled</>
                        : <><ToggleLeft size={22} /> Disabled</>
                      }
                    </button>
                    <button className="expand-toggle">
                      {expandedJobId === job.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                </div>

                {/* Expandable Details */}
                {expandedJobId === job.id && (
                  <div className="job-card-details">
                    <div className="detail-actions">
                      <button className="action-btn secondary" onClick={() => handleEditJob(job)}>
                        <Edit size={16} /> Edit Job
                      </button>
                      <button className="action-btn danger" onClick={() => handleDeleteJob(job.id)}>
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>

                    {/* Role Info Grid */}
                    <div className="job-details-grid">
                      <div className="detail-section">
                        <h4>Role Details</h4>
                        <div className="detail-items">
                          {job.role && (
                            <div className="detail-row">
                              <Briefcase size={16} />
                              <span className="detail-label">Role:</span>
                              <span className="detail-value">{job.role}</span>
                            </div>
                          )}
                          {job.reportingTo && (
                            <div className="detail-row">
                              <Users size={16} />
                              <span className="detail-label">Reporting To:</span>
                              <span className="detail-value">{job.reportingTo}</span>
                            </div>
                          )}
                          {job.teamName && (
                            <div className="detail-row">
                              <Building size={16} />
                              <span className="detail-label">Team:</span>
                              <span className="detail-value">{job.teamName}</span>
                            </div>
                          )}
                          <div className="detail-row">
                            <Clock size={16} />
                            <span className="detail-label">Employment Type:</span>
                            <span className="detail-value">{job.employmentType}</span>
                          </div>
                          <div className="detail-row">
                            <GraduationCap size={16} />
                            <span className="detail-label">Experience:</span>
                            <span className="detail-value">{job.experience}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Paragraph field */}
                    {job.aboutRole && (
                      <div className="detail-section full">
                        <h4>About the Team and the Role</h4>
                        <p className="detail-text">{job.aboutRole}</p>
                          
                      </div>
                    )}

                    {/* Bullet list fields */}
                    {job.responsibilities && (
                      <div className="detail-section full">
                        <h4>Responsibilities</h4>
                        <BulletDisplay items={job.responsibilities} />
                      </div>
                    )}

                    {job.experienceQualifications && (
                      <div className="detail-section full">
                        <h4>Experience and Qualifications</h4>
                        <BulletDisplay items={job.experienceQualifications} />
                      </div>
                    )}

                    {job.mustHave && (
                      <div className="detail-section full">
                        <h4>Must Have Competencies</h4>
                        <BulletDisplay items={job.mustHave} />
                      </div>
                    )}

                    {job.goodToHave && (
                      <div className="detail-section full">
                        <h4>Good to Have Competencies</h4>
                        <BulletDisplay items={job.goodToHave} />
                      </div>
                    )}

                    {/* Paragraph field */}
                    {job.additionalInfo && (
                      <div className="detail-section full">
                        <h4>Additional Information</h4>
                        <p className="detail-text whitespace-pre">{job.additionalInfo}</p>
                      </div>
                    )}

                    <div className="detail-section full">
                      <p className="created-info">
                        <Calendar size={14} />
                        Created on {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* ── Form Header ── */}
          <div className="job-posting-header">
            <div className="header-left">
              <div className="header-icon"><Briefcase size={32} /></div>
              <div className="header-info">
                <h1>{editingJobId ? 'Edit Job Posting' : 'Create Job Posting'}</h1>
                <p>{editingJobId ? 'Update the job posting details' : 'Post a new career opportunity'}</p>
              </div>
            </div>
            <div className="header-actions">
              <button className="action-button preview" onClick={() => setShowPreview(!showPreview)}>
                <Eye size={18} /> {showPreview ? 'Edit Mode' : 'Preview'}
              </button>
              <button className="action-button secondary" onClick={handleCancel}>
                <X size={18} /> Cancel
              </button>
            </div>
          </div>

          {!showPreview ? (
            <form onSubmit={handleSubmit} className="job-posting-form">

              {/* ── Job Overview ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><Briefcase size={20} /><h2>Job Overview</h2></div>
                  <span className="section-badge">Required</span>
                </div>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="jobTitle"><FileText size={16} /> Job Title *</label>
                    <input type="text" id="jobTitle" name="jobTitle" value={formData.jobTitle}
                      onChange={handleInputChange} placeholder="e.g., Senior Engineer - Embedded Hardware" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="category"><Building size={16} /> Category *</label>
                    <input type="text" id="category" name="category" value={formData.category}
                      onChange={handleInputChange} placeholder="e.g., Engineering, Sales" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="location"><MapPin size={16} /> Location *</label>
                    <input type="text" id="location" name="location" value={formData.location}
                      onChange={handleInputChange} placeholder="e.g., Bangalore or Remote" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="experience"><GraduationCap size={16} /> Experience *</label>
                    <input type="text" id="experience" name="experience" value={formData.experience}
                      onChange={handleInputChange} placeholder="e.g., 8-10 Years" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="employmentType"><Clock size={16} /> Employment Type *</label>
                    <select id="employmentType" name="employmentType" value={formData.employmentType}
                      onChange={handleInputChange} required>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                      <option value="Temporary">Temporary</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Role Details ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><Users size={20} /><h2>Role Details</h2></div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="role"><Briefcase size={16} /> Role</label>
                    <input type="text" id="role" name="role" value={formData.role}
                      onChange={handleInputChange} placeholder="e.g., Area Sales Manager" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reportingTo"><Users size={16} /> Reporting To</label>
                    <input type="text" id="reportingTo" name="reportingTo" value={formData.reportingTo}
                      onChange={handleInputChange} placeholder="e.g., Zonal Sales Manager" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="teamName"><Building size={16} /> Team Name</label>
                    <input type="text" id="teamName" name="teamName" value={formData.teamName}
                      onChange={handleInputChange} placeholder="e.g., Sales Team" />
                  </div>
                </div>
              </div>

              {/* ── About the Role (paragraph) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><FileText size={20} /><h2>About the Team and the Role</h2></div>
                  <span className="section-badge">Required</span>
                </div>
                <div className="form-group full-width">
                  <label htmlFor="aboutRole"><FileText size={16} /> Description *</label>
                  <textarea id="aboutRole" name="aboutRole" value={formData.aboutRole}
                    onChange={handleInputChange} rows="6"
                    placeholder="Describe the team and the role in detail..." required />
                </div>
               
                {/* <DynamicBulletField
                   label="Key Points (optional)"
                   icon={CheckCircle}
                   fieldName="aboutRolePoints"
                   items={formData.aboutRolePoints}
                    onChange={handleBulletChange}
                 /> */}

              </div>

              {/* ── Responsibilities (dynamic bullets) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><CheckCircle size={20} /><h2>Responsibilities</h2></div>
                  <span className="section-badge">Required</span>
                </div>
                <DynamicBulletField
                  label="Key Responsibilities"
                  icon={CheckCircle}
                  fieldName="responsibilities"
                  items={formData.responsibilities}
                  onChange={handleBulletChange}
                  required
                />
              </div>

              {/* ── Experience & Qualifications (dynamic bullets) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><Award size={20} /><h2>Experience and Qualifications</h2></div>
                  <span className="section-badge">Required</span>
                </div>
                <DynamicBulletField
                  label="Qualifications"
                  icon={GraduationCap}
                  fieldName="experienceQualifications"
                  items={formData.experienceQualifications}
                  onChange={handleBulletChange}
                  required
                />
              </div>

              {/* ── Must Have (dynamic bullets) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><Zap size={20} /><h2>Must Have Competencies</h2></div>
                  <span className="section-badge">Required</span>
                </div>
                <DynamicBulletField
                  label="Must Have"
                  icon={CheckCircle}
                  fieldName="mustHave"
                  items={formData.mustHave}
                  onChange={handleBulletChange}
                  required
                />
              </div>

              {/* ── Good to Have (dynamic bullets) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><Zap size={20} /><h2>Good to Have Competencies</h2></div>
                </div>
                <DynamicBulletField
                  label="Good to Have"
                  icon={Zap}
                  fieldName="goodToHave"
                  items={formData.goodToHave}
                  onChange={handleBulletChange}
                />
              </div>

              {/* ── Additional Info (paragraph) ── */}
              <div className="form-section">
                <div className="section-header">
                  <div className="section-title"><FileText size={20} /><h2>Additional Information</h2></div>
                </div>
                <div className="form-group full-width">
                  <label htmlFor="additionalInfo"><FileText size={16} /> Additional Info</label>
                  <textarea id="additionalInfo" name="additionalInfo" value={formData.additionalInfo}
                    onChange={handleInputChange} rows="5"
                    placeholder="Any other relevant information for candidates..." />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="action-btn publish">
                  <CheckCircle size={18} />
                  {editingJobId ? 'Update Job' : 'Publish Job'}
                </button>
              </div>
            </form>

          ) : (
            /* ── Preview ── */
            <div className="job-preview">
              <div className="preview-header">
                <h2>Job Posting Preview</h2>
                <p>This is how your job posting will appear to candidates</p>
              </div>
              <div className="preview-content">
                <div className="preview-title-section">
                  <h1>{formData.jobTitle || 'Job Title'}</h1>
                  <div className="preview-meta">
                    <span className="meta-item"><Building size={16} />{formData.category || 'Category'}</span>
                    <span className="meta-item"><MapPin size={16} />{formData.location || 'Location'}</span>
                    <span className="meta-item"><Clock size={16} />{formData.employmentType}</span>
                    <span className="meta-item"><GraduationCap size={16} />{formData.experience || 'Experience'}</span>
                  </div>
                </div>

                {(formData.reportingTo || formData.teamName) && (
                  <div className="preview-section">
                    {formData.role && <p><strong>Role:</strong> {formData.role}</p>}
                    {formData.reportingTo && <p><strong>Reporting To:</strong> {formData.reportingTo}</p>}
                    {formData.teamName && <p><strong>Team Name:</strong> {formData.teamName}</p>}
                  </div>
                )}

                {formData.aboutRole && (
                  <div className="preview-section">
                    <h3>About the Team and the Role</h3>
                    <p>{formData.aboutRole}</p>
                      {/* {formData.aboutRolePoints.some(Boolean) && (
      <BulletDisplay items={formData.aboutRolePoints} />
                  )} */}
                  </div>
                )}

                {formData.responsibilities.some(Boolean) && (
                  <div className="preview-section">
                    <h3>Responsibilities</h3>
                    <BulletDisplay items={formData.responsibilities} />
                  </div>
                )}

                {formData.experienceQualifications.some(Boolean) && (
                  <div className="preview-section">
                    <h3>Experience and Qualifications</h3>
                    <BulletDisplay items={formData.experienceQualifications} />
                  </div>
                )}

                {formData.mustHave.some(Boolean) && (
                  <div className="preview-section">
                    <h3>Must Have Competencies</h3>
                    <BulletDisplay items={formData.mustHave} />
                  </div>
                )}

                {formData.goodToHave.some(Boolean) && (
                  <div className="preview-section">
                    <h3>Good to Have Competencies</h3>
                    <BulletDisplay items={formData.goodToHave} />
                  </div>
                )}

                {formData.additionalInfo && (
                  <div className="preview-section">
                    <h3>Additional Information</h3>
                    <p className="whitespace-pre">{formData.additionalInfo}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Careerposting;

// import React, { useState, useEffect } from 'react';
// import { 
//   Briefcase, MapPin, DollarSign, Clock, Users, 
//   FileText, Calendar, X, Eye,
//   Building, GraduationCap, Award, Zap, CheckCircle,
//   Plus, Edit, Trash2, ChevronDown, ChevronUp
// } from 'lucide-react';
// import '../css/Careerposting.css';
// import axios from 'axios';

// const Careerposting = () => {
//   //const [jobs, setJobs] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [expandedJobId, setExpandedJobId] = useState(null);
//   const [editingJobId, setEditingJobId] = useState(null);
//   const [jobs, setJobs] = useState([]);

//   useEffect(() => {
//     const fetchjobs = async () => {
//       console.log('entered fetching jobs');
//       try {
//         const response = await axios.get('http://localhost:5000/api/careers');
//         console.log('response from fetch jobs', response.data);
//         setJobs(response.data.data);
        
//       } catch(error) {
//         alert('Failed to fetch jobs');
//         console.log('error for fetchjobs', error);
//       }
//     };
//     fetchjobs();
//   }, []);

 

//   const [formData, setFormData] = useState({
//     jobTitle: '',
//     category: '',
//     //department: '',
//     //location: '',
//     employmentType: 'Full-time',
//     location: '',
//     experience: '',
  
//    role: '',
//    reportingTo: '',
//    teamName: '',
   
//    aboutRole:'',
//     responsibilities: '',
//     experienceQualifications:'',
//     mustHave:'',
//     goodToHave:'',
//     additionalInfo:'',
  
//     status: 'Active'
//   });

//   const [showPreview, setShowPreview] = useState(false);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const resetForm = () => {
//     setFormData({
//       jobTitle: '',
//       department: '',
//       location: '',
//       employmentType: 'Full-time',
//       experienceLevel: 'Mid-level',
//       salaryMin: '',
//       salaryMax: '',
//       numberOfPositions: '1',
//       applicationDeadline: '',
//       jobDescription: '',
//       responsibilities: '',
//       requirements: '',
//       qualifications: '',
//       benefits: '',
//       skills: '',
//       status: 'Active'
//     });
//     setShowPreview(false);
//   };

//   const handleSubmit = async (e) => {
//   e.preventDefault();

//   try {
//     let res;

//     if (editingJobId) {
//       //  Update existing job
//       res = await axios.put(`http://localhost:5000/api/editcareerposts/${editingJobId}`, formData);
//       console.log('Update response:', res.data);

//       // Update job in local state
//       setJobs(prevJobs =>
//         prevJobs.map(job => job.id === editingJobId ? res.data : job)
//       );

//       alert('Job updated successfully!');
//       setEditingJobId(null);
//     } else {
//       //  Create new job
//       res = await axios.post('http://localhost:5000/api/careerposts', formData);
//       console.log('Create response:', res.data);

//       // Add new job to local state
//       setJobs(prevJobs => [...prevJobs, res.data]);
      

//       alert('Job posted successfully!');
//     }
//   } catch (error) {
//     alert("Job submission failed");
//     console.error('Submit error:', error);
//   }

//   resetForm();
//   setShowForm(false);
// };


//   const handleAddNew = () => {
//     resetForm();
//     setEditingJobId(null);
//     setShowForm(true);
//     setExpandedJobId(null);
//   };

//   const handleCancel = () => {
//     if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
//       resetForm();
//       setShowForm(false);
//       setEditingJobId(null);
//     }
//   };

//   const handleEditJob = (job) => {
//     // setFormData(job);
//       setFormData({
//     jobTitle: job.jobTitle || '',
//     category: job.category || '',
//     employmentType: job.employmentType || 'Full-time',
//     location: job.location || '',
//     experience: job.experience || '',

//     role: job.role || '',
//     reportingTo: job.reportingTo || '',
//     teamName: job.teamName || '',

//     aboutRole: job.aboutRole || '',
//     responsibilities: job.responsibilities || '',
//     experienceQualifications: job.experienceQualifications || '',
//     mustHave: job.mustHave || '',
//     goodToHave: job.goodToHave || '',
//     additionalInfo: job.additionalInfo || '',

//     status: job.status || 'Active'
//   });

//     setEditingJobId(job.id);
//     setShowForm(true);
//     setExpandedJobId(null);
//   };

//   const handleDeleteJob = async(jobId) => {
//     try {
//     if (window.confirm('Are you sure you want to delete this job posting?')) {
//       const res = await axios.delete(`http://localhost:5000/api/deletecareer/${jobId}`);
//       setJobs(jobs.filter(job => job.id !== jobId));
//       alert('Job deleted successfully!');
//     }
//   } catch(err) {
//     console.log('error', err);
//     alert('Failed to delete the job');
//   }
//   };

//   const toggleJobDetails = (jobId) => {
//     setExpandedJobId(expandedJobId === jobId ? null : jobId);
//   };

//   if (jobs.length === 0 && !showForm) {
//     return (
//       <div className="job-posting-container">
//         <div className="empty-state">
//           <div className="empty-icon">
//             <Briefcase size={64} />
//           </div>
//           <h2>No Job Postings Yet</h2>
//           <p>Start by creating your first job posting to attract talented candidates</p>
//           <button className="add-job-btn primary" onClick={handleAddNew}>
//             <Plus size={20} />
//             Add Your First Job
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="job-posting-container">
//       {!showForm ? (
//         <>
//           {/* Header with job list */}
//           <div className="jobs-header">
//             <div className="header-info">
//               <h1>Job Postings</h1>
//               <p>Manage all your career opportunities</p>
//             </div>
//             <button className="add-job-btn primary" onClick={handleAddNew}>
//               <Plus size={20} />
//               Add New Job
//             </button>
//           </div>

//           {/* Jobs List */}
//           <div className="jobs-list">
//             {jobs.map((job) => (
//               <div key={job.id} className={`job-card ${expandedJobId === job.id ? 'expanded' : ''}`}>
//                 {/* Job Card Header - Always Visible */}
//                 <div className="job-card-header" onClick={() => toggleJobDetails(job.id)}>
//                   <div className="job-card-left">
//                     <div className="job-icon">
//                       <Briefcase size={24} />
//                     </div>
//                     <div className="job-basic-info">
//                       <h3 className="job-title">{job.jobTitle}</h3>
//                       <div className="job-meta-quick">
//                         <span className="meta-badge">
//                           <Building size={14} />
//                           {job.department}
//                         </span>
//                         <span className="meta-badge">
//                           <MapPin size={14} />
//                           {job.location}
//                         </span>
//                         <span className="meta-badge">
//                           <Users size={14} />
//                           {job.numberOfPositions} {job.numberOfPositions === '1' ? 'Position' : 'Positions'}
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="job-card-right">
//                     <span className={'job-status-badge'}>
//                       {job.status}
//                     </span>
//                     <button className="expand-toggle">
//                       {expandedJobId === job.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
//                     </button>
//                   </div>
//                 </div>

//                 {/* Job Card Details - Expandable */}
//                 {expandedJobId === job.id && (
//                   <div className="job-card-details">
//                     {/* Action Buttons */}
//                     <div className="detail-actions">
//                       <button className="action-btn secondary" onClick={() => handleEditJob(job)}>
//                         <Edit size={16} />
//                         Edit Job
//                       </button>
//                       <button className="action-btn danger" onClick={() => handleDeleteJob(job.id)}>
//                         <Trash2 size={16} />
//                         Delete
//                       </button>
//                     </div>

//                     {/* Job Details Grid */}
//                     <div className="job-details-grid">
//                       <div className="detail-section">
//                         <h4>Employment Details</h4>
//                         <div className="detail-items">
//                           <div className="detail-row">
//                             <Clock size={16} />
//                             <span className="detail-label">Employment Type:</span>
//                             <span className="detail-value">{job.employmentType}</span>
//                           </div>
//                           <div className="detail-row">
//                             <GraduationCap size={16} />
//                             <span className="detail-label">Experience Level:</span>
//                             <span className="detail-value">{job.experienceLevel}</span>
//                           </div>
//                           <div className="detail-row">
//                             <Calendar size={16} />
//                             <span className="detail-label">Application Deadline:</span>
//                             <span className="detail-value">{job.applicationDeadline}</span>
//                           </div>
//                         </div>
//                       </div>

//                       {(job.salaryMin || job.salaryMax) && (
//                         <div className="detail-section">
//                           <h4>Compensation</h4>
//                           <div className="salary-info">
//                             <DollarSign size={20} />
//                             <span className="salary-range">
//                               ${job.salaryMin || '—'} - ${job.salaryMax || '—'}
//                             </span>
//                             <span className="salary-period">per year</span>
//                           </div>
//                         </div>
//                       )}
//                     </div>

//                     {job.jobDescription && (
//                       <div className="detail-section full">
//                         <h4>Job Description</h4>
//                         <p className="detail-text">{job.jobDescription}</p>
//                       </div>
//                     )}

//                     {job.responsibilities && (
//                       <div className="detail-section full">
//                         <h4>Key Responsibilities</h4>
//                         <p className="detail-text whitespace-pre">{job.responsibilities}</p>
//                       </div>
//                     )}

//                     {job.requirements && (
//                       <div className="detail-section full">
//                         <h4>Requirements</h4>
//                         <p className="detail-text whitespace-pre">{job.requirements}</p>
//                       </div>
//                     )}

//                     {job.qualifications && (
//                       <div className="detail-section full">
//                         <h4>Preferred Qualifications</h4>
//                         <p className="detail-text whitespace-pre">{job.qualifications}</p>
//                       </div>
//                     )}

//                     {job.skills && (
//                       <div className="detail-section full">
//                         <h4>Required Skills</h4>
//                         <div className="skills-list">
//                           {job.skills.split(',').map((skill, index) => (
//                             <span key={index} className="skill-tag">{skill.trim()}</span>
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     {job.benefits && (
//                       <div className="detail-section full">
//                         <h4>Benefits & Perks</h4>
//                         <p className="detail-text whitespace-pre">{job.benefits}</p>
//                       </div>
//                     )}

//                     <div className="detail-section full">
//                       <p className="created-info">
//                         <Calendar size={14} />
//                         {/* Created on {job.createdAt} */}
//                         Created on {new Date(job.createdAt).toLocaleDateString()}
//                       </p>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </>
//       ) : (
//         <>
//           {/* Job Posting Form */}
//           <div className="job-posting-header">
//             <div className="header-left">
//               <div className="header-icon">
//                 <Briefcase size={32} />
//               </div>
//               <div className="header-info">
//                 <h1>{editingJobId ? 'Edit Job Posting' : 'Create Job Posting'}</h1>
//                 <p>{editingJobId ? 'Update the job posting details' : 'Post a new career opportunity'}</p>
//               </div>
//             </div>
//             <div className="header-actions">
//               <button className="action-button preview" onClick={() => setShowPreview(!showPreview)}>
//                 <Eye size={18} />
//                 {showPreview ? 'Edit Mode' : 'Preview'}
//               </button>
//               <button className="action-button secondary" onClick={handleCancel}>
//                 <X size={18} />
//                 Cancel
//               </button>
//             </div>
//           </div>

//           {!showPreview ? (
//             <form onSubmit={handleSubmit} className="job-posting-form">
//               {/* Basic Information Section */}
//               <div className="form-section">
//                 <div className="section-header">
//                   <div className="section-title">
//                     <Briefcase size={20} />
//                     <h2>Job Overview</h2>
//                   </div>
//                   <span className="section-badge">Required</span>
//                 </div>

//                 <div className="form-grid">
//                   <div className="form-group full-width">
//                     <label htmlFor="jobTitle">
//                       <FileText size={16} />
//                       Job Title *
//                     </label>
//                     <input
//                       type="text"
//                       id="jobTitle"
//                       name="jobTitle"
//                       value={formData.jobTitle}
//                       onChange={handleInputChange}
//                       placeholder="e.g., Senior Software Engineer"
//                       required
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="category">
//                       <Building size={16} />
//                       Category *
//                     </label>
//                     <input
//                       type="text"
//                       id="category"
//                       name="category"
//                       value={formData.category}
//                       onChange={handleInputChange}
//                       placeholder="e.g., Engineering, Marketing"
//                       required
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="location">
//                       <MapPin size={16} />
//                       Location *
//                     </label>
//                     <input
//                       type="text"
//                       id="location"
//                       name="location"
//                       value={formData.location}
//                       onChange={handleInputChange}
//                       placeholder="e.g., San Francisco, CA or Remote"
//                       required
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="experience">
//                       <MapPin size={16} />
//                       Experience *
//                     </label>
//                     <input
//                       type="text"
//                       id="experience"
//                       name="experience"
//                       value={formData.experience}
//                       onChange={handleInputChange}
//                       placeholder="e.g., 2 Years"
//                       required
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="employmentType">
//                       <Clock size={16} />
//                       Employment Type *
//                     </label>
//                     <select
//                       id="employmentType"
//                       name="employmentType"
//                       value={formData.employmentType}
//                       onChange={handleInputChange}
//                       required
//                     >
//                       <option value="Full-time">Full-time</option>
//                       <option value="Part-time">Part-time</option>
//                       <option value="Contract">Contract</option>
//                       <option value="Internship">Internship</option>
//                       <option value="Temporary">Temporary</option>
//                     </select>
//                   </div>

                 
//                 </div>
//               </div>

//               {/* Compensation & Details Section */}
//               <div className="form-section">
//                 <div className="section-header">
//                   <div className="section-title">
//                     <DollarSign size={20} />
//                     <h2>Role Details</h2>
//                   </div>
//                 </div>

//                 <div className="form-grid">
//                   <div className="form-group">
//                     <label htmlFor="role">
//                       <DollarSign size={16} />
//                       Role
//                     </label>
//                     <input
//                       type="number"
//                       id="role"
//                       name="role"
//                       value={formData.role}
//                       onChange={handleInputChange}
//                       placeholder="Role (e.g., Area Sales Manager"
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="reportingTo">
//                       <DollarSign size={16} />
//                       Reporting to
//                     </label>
//                     <input
//                       type="number"
//                       id="reportingTo"
//                       name="reportingTo"
//                       value={formData.reportingTo}
//                       onChange={handleInputChange}
//                       placeholder="Reporting To"
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="numberOfPositions">
//                       <Users size={16} />
//                       Number of Positions *
//                     </label>
//                     <input
//                       type="number"
//                       id="numberOfPositions"
//                       name="numberOfPositions"
//                       value={formData.numberOfPositions}
//                       onChange={handleInputChange}
//                       min="1"
//                       required
//                     />
//                   </div>

//                   <div className="form-group">
//                     <label htmlFor="applicationDeadline">
//                       <Calendar size={16} />
//                       Application Deadline *
//                     </label>
//                     <input
//                       type="date"
//                       id="applicationDeadline"
//                       name="applicationDeadline"
//                       value={formData.applicationDeadline}
//                       onChange={handleInputChange}
//                       required
//                     />
//                   </div>
//                 </div>
//               </div>

//               {/* Job Description Section */}
//               <div className="form-section">
//                 <div className="section-header">
//                   <div className="section-title">
//                     <FileText size={20} />
//                     <h2>Job Description</h2>
//                   </div>
//                   <span className="section-badge">Required</span>
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="jobDescription">
//                     <FileText size={16} />
//                     Job Description *
//                   </label>
//                   <textarea
//                     id="jobDescription"
//                     name="jobDescription"
//                     value={formData.jobDescription}
//                     onChange={handleInputChange}
//                     rows="6"
//                     placeholder="Provide a detailed description of the job role..."
//                     required
//                   />
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="responsibilities">
//                     <CheckCircle size={16} />
//                     Key Responsibilities *
//                   </label>
//                   <textarea
//                     id="responsibilities"
//                     name="responsibilities"
//                     value={formData.responsibilities}
//                     onChange={handleInputChange}
//                     rows="6"
//                     placeholder="List the main responsibilities (one per line or use bullet points)..."
//                     required
//                   />
//                 </div>
//               </div>

//               {/* Requirements Section */}
//               <div className="form-section">
//                 <div className="section-header">
//                   <div className="section-title">
//                     <Award size={20} />
//                     <h2>Requirements & Qualifications</h2>
//                   </div>
//                   <span className="section-badge">Required</span>
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="requirements">
//                     <Award size={16} />
//                     Requirements *
//                   </label>
//                   <textarea
//                     id="requirements"
//                     name="requirements"
//                     value={formData.requirements}
//                     onChange={handleInputChange}
//                     rows="6"
//                     placeholder="List the required qualifications and experience..."
//                     required
//                   />
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="qualifications">
//                     <GraduationCap size={16} />
//                     Preferred Qualifications
//                   </label>
//                   <textarea
//                     id="qualifications"
//                     name="qualifications"
//                     value={formData.qualifications}
//                     onChange={handleInputChange}
//                     rows="4"
//                     placeholder="List any preferred or nice-to-have qualifications..."
//                   />
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="skills">
//                     <Zap size={16} />
//                     Required Skills *
//                   </label>
//                   <input
//                     type="text"
//                     id="skills"
//                     name="skills"
//                     value={formData.skills}
//                     onChange={handleInputChange}
//                     placeholder="e.g., JavaScript, React, Node.js, Python (comma separated)"
//                     required
//                   />
//                   <span className="input-hint">Separate skills with commas</span>
//                 </div>
//               </div>

//               {/* Benefits Section */}
//               <div className="form-section">
//                 <div className="section-header">
//                   <div className="section-title">
//                     <Award size={20} />
//                     <h2>Benefits & Perks</h2>
//                   </div>
//                 </div>

//                 <div className="form-group full-width">
//                   <label htmlFor="benefits">
//                     <Award size={16} />
//                     Benefits & Perks
//                   </label>
//                   <textarea
//                     id="benefits"
//                     name="benefits"
//                     value={formData.benefits}
//                     onChange={handleInputChange}
//                     rows="5"
//                     placeholder="List the benefits and perks offered (e.g., health insurance, remote work, etc.)..."
//                   />
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div className="form-actions">
//                 <button type="submit" className="action-btn publish">
//                   <CheckCircle size={18} />
//                   {editingJobId ? 'Update Job' : 'Publish Job'}
//                 </button>
//               </div>
//             </form>
//           ) : (
//             <div className="job-preview">
//               <div className="preview-header">
//                 <h2>Job Posting Preview</h2>
//                 <p>This is how your job posting will appear to candidates</p>
//               </div>

//               <div className="preview-content">
//                 <div className="preview-title-section">
//                   <h1>{formData.jobTitle || 'Job Title'}</h1>
//                   <div className="preview-meta">
//                     <span className="meta-item">
//                       <Building size={16} />
//                       {formData.department || 'Department'}
//                     </span>
//                     <span className="meta-item">
//                       <MapPin size={16} />
//                       {formData.location || 'Location'}
//                     </span>
//                     <span className="meta-item">
//                       <Clock size={16} />
//                       {formData.employmentType}
//                     </span>
//                     <span className="meta-item">
//                       <GraduationCap size={16} />
//                       {formData.experienceLevel}
//                     </span>
//                   </div>
//                 </div>

//                 {(formData.salaryMin || formData.salaryMax) && (
//                   <div className="preview-section">
//                     <h3>Compensation</h3>
//                     <p className="salary-range">
//                       ${formData.salaryMin || '—'} - ${formData.salaryMax || '—'} per year
//                     </p>
//                   </div>
//                 )}

//                 {formData.jobDescription && (
//                   <div className="preview-section">
//                     <h3>Job Description</h3>
//                     <p>{formData.jobDescription}</p>
//                   </div>
//                 )}

//                 {formData.responsibilities && (
//                   <div className="preview-section">
//                     <h3>Key Responsibilities</h3>
//                     <p className="whitespace-pre">{formData.responsibilities}</p>
//                   </div>
//                 )}

//                 {formData.requirements && (
//                   <div className="preview-section">
//                     <h3>Requirements</h3>
//                     <p className="whitespace-pre">{formData.requirements}</p>
//                   </div>
//                 )}

//                 {formData.qualifications && (
//                   <div className="preview-section">
//                     <h3>Preferred Qualifications</h3>
//                     <p className="whitespace-pre">{formData.qualifications}</p>
//                   </div>
//                 )}

//                 {formData.skills && (
//                   <div className="preview-section">
//                     <h3>Required Skills</h3>
//                     <div className="skills-list">
//                       {formData.skills.split(',').map((skill, index) => (
//                         <span key={index} className="skill-tag">{skill.trim()}</span>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {formData.benefits && (
//                   <div className="preview-section">
//                     <h3>Benefits & Perks</h3>
//                     <p className="whitespace-pre">{formData.benefits}</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </>
//       )}
//     </div>
//   );
// };

// export default Careerposting;